import React, { FC } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useGetResourceEventsQuery } from '../healthWalletSlice';
import { formatDate } from '../healthWalletConstants';
import { V1_ACCENT } from './theme';
import type { GroupedSummary } from '../healthWalletConstants';
import { EventTimeline } from '../EventTimeline';
import { DocHistoryError } from './DocHistoryError';
import { statusChip } from './classify';

// ============================================================================
// Conditions detail dialog (RendererV1) — the design's frame (header w/ status
// chip, fact row, "About this record" template prose) with TODAY'S shared
// EventTimeline embedded as the documentation history (recorded decision: the
// period-bucketed timeline with encounter-linked visit titles = PHASE 1b).
// Events technical details already ride each timeline row.
// ============================================================================

export const ConditionDetailDialog: FC<{
  group: GroupedSummary | null;
  open: boolean;
  onClose: () => void;
}> = ({ group, open, onClose }) => {
  // The conditions drill-down is the code-scoped VISIT trail (get_patient_conditions_events): it scopes by the
  // card's CODE SET, not the display name. We reuse the events `name` channel (no endpoint change) by PACKING the
  // card identity as name ⊕ \x1f ⊕ codes… — token 1 = name, tokens 2+ = standard codes. Other/nameless → '' key.
  const eventsKey = !group || group.name === 'Other / Uncategorized'
    ? ''
    : [group.name, ...(group.codes ?? [])].join('\x1f');
  const { data: events, isLoading, isFetching, isError, refetch } = useGetResourceEventsQuery(
    { resource: 'conditions', name: eventsKey },
    { skip: !group || !open }
  );
  if (!group) return null;
  const chip = statusChip(group);
  const loading = isLoading || isFetching;
  const fact = (label: string, value: string, bordered = false) => (
    <Box sx={{ flex: 1, minWidth: 120, ...(bordered ? { borderLeft: '1px solid #f0f0f3', pl: 2 } : {}) }}>
      <Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.5 }}>{value}</Typography>
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0}
      PaperProps={{ sx: { borderRadius: '22px', bgcolor: '#f7f5fb', overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', p: '24px 26px', borderBottom: '1px solid #ece9f4', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15 }}>{group.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
            {chip ? (
              <Box sx={{ fontSize: '12.5px', fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '20px', bgcolor: chip.bg, color: chip.fg }}>{chip.label}</Box>
            ) : null}
            <Typography sx={{ fontSize: '12.5px', color: '#8e8e93' }}>as documented in your health record</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ bgcolor: '#ececed', color: '#6b6b70' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ p: '22px !important' }}>
        {/* Fact row */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'white' }}>
          {/* Same calendar day for first & last → show one "Date recorded" (formatDate is day-level). */}
          {group.firstDate && formatDate(group.firstDate) === formatDate(group.lastDate) ? (
            fact('Date recorded', formatDate(group.firstDate))
          ) : (
            <>
              {fact('First documented', group.firstDate ? formatDate(group.firstDate) : '—')}
              {fact('Most recently documented', group.lastDate ? formatDate(group.lastDate) : '—', true)}
            </>
          )}
        </Paper>

        {/* About this record — template prose (NOT AI), mirrors the design's generated copy. */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 1 }}>About this record</Typography>
          <Typography sx={{ fontSize: '14.5px', lineHeight: 1.55, color: '#2a2a2c' }}>
            This record has been documented by your healthcare providers across multiple visits
            {group.firstDate ? `, first appearing in ${formatDate(group.firstDate)}` : ''}. This page summarizes how it appears
            in your health record; it does not interpret the condition.
          </Typography>
        </Paper>

        {/* Documentation history — shared EventTimeline (per-event expandable details incl. technical). */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px 8px', bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 2 }}>Documentation history</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Box>
          ) : isError ? (
            <DocHistoryError onRetry={refetch} />
          ) : (
            <EventTimeline eventList={events ?? []} chartData={null} resource="conditions" groupName={group.name} growWithContent hideHeaderOnExpand />
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};
